import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StripePayment from "../../common/components/PaymentStep/StripePayment";
import { useDispatch, useSelector } from "react-redux";
import routes from "../../../../routes/routes";
import { useNavigate } from "react-router-dom";
import {
  bookingCreationCompleted,
  initializePaymentProvider,
  paymentInitializationRetryStarted,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { RootState } from "../../../../redux/store";
import InitializationErrorInfo from "../../common/components/PaymentStep/InitializationErrorInfo";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import NoPayment from "../../common/components/PaymentStep/NoPayment";

interface PaymentLoadingIndicatorProps {
  messageKey?: string;
}

const PaymentLoadingIndicator = ({
  messageKey = "general.voucher_purchase.preparing_payment",
}: PaymentLoadingIndicatorProps) => {
  const { t } = useTranslation();
  return (
    <>
      <LoaderOverlay open={true} />
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-x-0 bottom-0 z-[3] mx-auto max-w-[1000px] px-6 pb-[calc(40px+env(safe-area-inset-bottom,0))] pointer-events-none"
      >
        <p className="text-center text-sm text-foreground/80">
          {t(messageKey)}
        </p>
      </div>
    </>
  );
};

interface VoucherPaymentStepProps {}

const VoucherPaymentStep = (props: VoucherPaymentStepProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentProvider = useSelector(
    (state: RootState) => state.bookingCreation.payment.provider
  );
  const paymentInitFailed = useSelector(
    (state: RootState) => state.bookingCreation.payment.paymentInitFailed
  );
  const stripePayment = useSelector(
    (state: RootState) => state.bookingCreation.payment.stripe
  );
  const orderIds = useSelector(
    (state: RootState) => state.bookingCreation.payment.orderIds
  );
  const reservations = useSelector(
    (state: RootState) => state.bookingCreation.reservations
  )!;
  const { voucherAmount } = useSelector(
    (state: RootState) => state.bookingCreation
  )!;
  const [isLoading, setIsLoading] = useState(true);
 

  useEffect(() => {
    if (orderIds?.length) {
      navigate(routes.orders);
      setTimeout(() => dispatch(bookingCreationCompleted()), 1000);
    }
  }, [orderIds]);

  useEffect(() => {
    if (voucherAmount === 0) {
      dispatch(initializePaymentProvider("no_payment"));
    } else {
      dispatch(initializePaymentProvider("stripe"));
    }

    setIsLoading(false);
  }, [voucherAmount, dispatch, reservations, paymentProvider]);

  if (isLoading || !paymentProvider) {
    return <PaymentLoadingIndicator />;
  }

  if (paymentProvider !== "no_payment" && paymentInitFailed) {
    const showTryAgain = Boolean(
      paymentProvider === "stripe" &&
        stripePayment.token &&
        stripePayment?.checkoutFrontendUrl
    );

    return (
      <InitializationErrorInfo
        showTryAgain={showTryAgain}
        onBackClick={() => navigate(routes.home)}
        onTryAgainClick={() => dispatch(paymentInitializationRetryStarted({}))}
      />
    );
  }

  if (paymentProvider === "stripe") {
    return <StripePayment service={"voucher"} />;
  }
  if (paymentProvider === "no_payment") {
    return <NoPayment service={"voucher"} />;
  }

  return <PaymentLoadingIndicator />;
};

export default VoucherPaymentStep;
